import axios from "axios";

import {
  Select2SelectListItem,
  Quotation,
  FindDistributorPagedListResponse,
} from "src/types";
import { getRemoteList } from "./Shared";
import getFlattenedKeyValues from "./getFlattenedKeyValues";
import { ProductDetails } from "./components/ProductRegistrationForm";

export async function validateProductNumber(
  productNumber: string
): Promise<ProductDetails> {
  if (!productNumber) {
    throw new Error("No product number");
  }

  const response = await axios.post(
    "/Product/Registration/ValidateProductNumber",
    {
      ProductNumber: productNumber,
    }
  );
  if (response && response.data && response.data.IsSuccessful) {
    return {
      productFamily: response.data.ProductFamilyDescription,
      productDescription: response.data.ProductDescription,
      requireDongleNumber: response.data.RequireDongleNumber,
    };
  }

  throw response;
}

export async function findDistributors(
  pageSize: number,
  pageNumber: number,
  quotationId: string,
  zipCode: string,
  distributorName: string,
  returnFavoritesOnly: boolean,
  productGroups: string[]
) {
  const response = await axios.post<FindDistributorPagedListResponse>(
    "/General/FindDistributor/FindDistributorsJson",
    {
      PageSize: pageSize,
      PageNumber: pageNumber,
      QuotationId: quotationId,
      ZipCode: zipCode,
      DistributorName: distributorName,
      ReturnFavoritesOnly: returnFavoritesOnly,
      ProductGroups: productGroups,
    }
  );
  if (response && response.data && response.data.IsSuccessful) {
    return response;
  }

  throw response;
}

export async function toggleFavoriteDistributor(
  distributorLocationId: number,
  newIsFavorite: boolean
) {
  const response = await axios.post<FindDistributorPagedListResponse>(
    "/General/FindDistributor/ToggleFavoriteDistributor",
    {
      DistributorLocationId: distributorLocationId,
      NewIsFavorite: newIsFavorite,
    }
  );
  if (response && response.data && response.data.IsSuccessful) {
    return response;
  }

  throw response;
}

export async function registerProduct(
  productNumber: string,
  serialNumber: string,
  purchaseDate: string,
  dongleNumber: string,
  isCompanyOwned: boolean,
  promoCode: string
) {
  if (!productNumber) {
    throw new Error("No product number");
  }

  if (!serialNumber) {
    throw new Error("No serial number");
  }

  const response = await axios.post("/Product/Registration/Register", {
    ProductNumber: productNumber,
    SerialNumber: serialNumber,
    PurchaseDate: purchaseDate,
    DongleNumber: dongleNumber,
    IsCompanyOwned: isCompanyOwned,
    PromoCode: promoCode,
  });
  if (response && response.data && response.data.IsSuccessful) {
    return true;
  }

  throw response;
}

export async function removeItemFromShoppingCart(
  shoppingCartItemTypeCode: string,
  details: { shoppingCartItemIdentifier: string; totalQuantityRemoved: number },
  registrantEmail: string
) {
  const { shoppingCartItemIdentifier, totalQuantityRemoved } = details;

  try {
    const response = await axios.post("/Cart/ShoppingCart/RemoveItem", {
      ShoppingCartItemTypeCode: shoppingCartItemTypeCode,
      ShoppingCartItemIdentifier: shoppingCartItemIdentifier,
      RegistrantEmail: registrantEmail
    });

    if (response && response.data && response.data.IsSuccessful) {
      //
      // Decrement the number from the counter on the page
      //
      var cartCounter = document.getElementById("js-num-shopping-cart-items");
      var currentNumCartItems = parseInt(cartCounter.innerText);
      var newNumCartItems = currentNumCartItems - totalQuantityRemoved;
      cartCounter.innerHTML = `${newNumCartItems}`;

      // Hide the counter if it's empty now
      if (newNumCartItems === 0) {
        cartCounter.classList.add("d-none");
      }

      toastr.success("Removed item from your shopping cart.", "Success");

      return response.data;
    } else {
      throw response;
    }
  } catch (error) {
    console.error(error);
    toastr.error("Failed to remove item from your shopping cart.", "Error");
    throw error;
  }
}

export async function setActiveQuotation(quotationId: number) {
  try {
    const response = await axios.post(
      `/Quotation/Quotation/SetActiveQuotation?Id=${quotationId}&goToQuotationDetails=false`
    );
    if (response && response.data && response.data === "OK") {
      return;
    } else {
      throw response;
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function removeProductsFromQuotation(
  quotationId: number,
  quotationProductIds: number[],
  totalQuantityRemoved: number,
  isShoppingCart: boolean
) {
  try {
    const response = await axios.post("/QuotationProduct/Delete", {
      quoteId: quotationId,
      quotationProductIds: quotationProductIds,
    });

    if (response && response.data && response.data.IsSuccessful) {
      if (isShoppingCart) {
        //
        // Decrement the number from the counter on the page
        //
        var cartCounter = document.getElementById("js-num-shopping-cart-items");
        var currentNumCartItems = parseInt(cartCounter.innerText);
        var newNumCartItems = currentNumCartItems - totalQuantityRemoved;
        cartCounter.innerHTML = `${newNumCartItems}`;
      }

      return;
    } else {
      throw response;
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getStates() {
  return getRemoteList<Select2SelectListItem>(
    `/SelectList/AppCodeDetailSelectList/List?CodeGroup=Application.State`
  ).then((items) => items.map((item) => item.text));
}

function createHiddenField(name: string, value: any) {
  const hiddenField = document.createElement("input");
  hiddenField.type = "hidden";
  hiddenField.name = name;
  hiddenField.value = value;
  return hiddenField;
}

export function postPageTo(url: string, data: Record<string, any> = undefined) {
  return new Promise((resolve, reject) => {
    const form = document.createElement("form");
    form.name = "PostPageToUrl";
    form.method = "POST";
    form.action = url;

    //
    // Add hidden fields for any simple values to post up
    //
    const flattenedKeyValues = getFlattenedKeyValues(data);
    for (const flattenedKeyValue of flattenedKeyValues) {
      const hiddenField = createHiddenField(
        flattenedKeyValue.Key,
        flattenedKeyValue.Value
      );
      form.appendChild(hiddenField);
    }

    document.body.appendChild(form);
    form.submit();

    // purposely never resolve since the page will load to somewhere else
  });
}

function getAbsoluteUrl(url: string | undefined) {
  const absoluteUrl =
    typeof url === "string" &&
    !url.startsWith("https://") &&
    !url.startsWith("http://")
      ? `${window.location.origin}${url}`
      : url;

  return absoluteUrl;
}

export async function saveQuotationDetails(
  quotation: Quotation,
  saveUrl: string,
  returnUrl: string | undefined,
  setActiveQuotation?: boolean
) {
  try {
    await postPageTo(saveUrl, {
      model: {
        QuotationId: quotation.QuotationId,
        ReturnUrl: getAbsoluteUrl(returnUrl),
        Details: {
          ...quotation,
          SetActiveQuotation: !!setActiveQuotation,
        },
      },
    });
  } catch (error) {
    console.error(error);
    toastr.error("Failed to save details for quotation.");
    throw error;
  }
}

export async function validatePromotionCode(promotionCode: string) {
  if (!promotionCode) {
    toastr.error("No promotion code");
  } else {
    const response = await axios.post(
      "/Cart/ShoppingCart/ValidatePromotionCode",
      {
        PromotionCode: promotionCode,
      }
    );

    if (response && response.data && response.data.IsSuccessful) {
      if (response.data.IsPromotionCodeValid) {
        toastr.success("Applied Promo Code");
      } else {
        toastr.error(response.data.Message);
      }
      return {
        ...response.data,
      };
    }
  }
}